import {
  AnimationProperty,
  CaptionsAnimationConfig,
  CaptionsAnimationName,
} from '@sparemin/text-effects';
import { fromJS } from 'immutable';
import { useCallback } from 'react';
import { useTextOverlay } from '../../state/TextOverlayProvider';
import { TextOverlayState } from '../../state/types';

export interface UseTextOverlayAdvancedAnimation
  extends Pick<TextOverlayState, 'onTurnOnAdvancedAnimation'> {
  selectedAdvancedAnimation: CaptionsAnimationConfig | undefined;
  onActiveAnimationChange: (animationName: CaptionsAnimationName) => void;
  onAnimationPropertyChange: (
    animationProperty: AnimationProperty,
    value: string,
  ) => void;
  onTurnAnimationsOff: () => void;
}

const useTextOverlayAdvancedAnimation = (): UseTextOverlayAdvancedAnimation => {
  const {
    draftEditorData,
    onUpdateOverlay,
    onTurnOnAdvancedAnimation,
  } = useTextOverlay();

  const selectedAdvancedAnimation = draftEditorData
    .get('advancedAnimation')
    ?.toJS();

  const handleActiveAnimationChange = useCallback(
    (animationName: CaptionsAnimationName): void => {
      onUpdateOverlay(advancedAnimation => {
        return advancedAnimation.set(
          'advancedAnimation',
          fromJS({
            ...selectedAdvancedAnimation,
            animationName,
          }),
        );
      });
    },
    [onUpdateOverlay, selectedAdvancedAnimation],
  );

  const handleAnimationPropertyChange = useCallback(
    (animationProperty: AnimationProperty, value: string): void => {
      onUpdateOverlay(advancedAnimation => {
        return advancedAnimation.set(
          'advancedAnimation',
          fromJS({
            ...selectedAdvancedAnimation,
            properties: {
              ...selectedAdvancedAnimation?.properties,
              [animationProperty]: value,
            },
          }),
        );
      });
    },
    [onUpdateOverlay, selectedAdvancedAnimation],
  );

  const handleTurnAnimationsOff = useCallback((): void => {
    onUpdateOverlay(advancedAnimation => {
      return advancedAnimation.set(
        'advancedAnimation',
        fromJS({
          enabled: false,
        }),
      );
    });
  }, [onUpdateOverlay]);

  return {
    selectedAdvancedAnimation,
    onTurnOnAdvancedAnimation,
    onActiveAnimationChange: handleActiveAnimationChange,
    onAnimationPropertyChange: handleAnimationPropertyChange,
    onTurnAnimationsOff: handleTurnAnimationsOff,
  };
};

export default useTextOverlayAdvancedAnimation;
